// Utility functions for mapping customer_interaction data to visit data
import { getWIBTimeISO } from './timeUtils';

export interface CustomerInteraction {
  id: number;
  No_pelanggan: string;
  customer_name: string;
  customer_tambak_id?: number;
  date_created: string;
  scheduled_date?: string;
  completed_date?: string;
  subject: string;
  keterangan?: string;
  interaction_type: 'WhatsApp' | 'Phone Call' | 'Visit/Meeting';
  status: 'completed' | 'scheduled' | 'pending' | 'cancelled';
  priority: 'low' | 'medium' | 'high';
  PIC: string;
  visit_id?: number;
  duration_minutes?: number;
  follow_up_required: boolean;
  follow_up_date?: string;
}

export interface MappedVisitData {
  id: string;
  employee_id: string;
  customer_id: number;
  customer_name: string;
  customer_address: string;
  start_time: string;
  status: 'active';
  notes: string;
  satisfaction_rating: number;
  latitude: number;
  longitude: number;
  visit_coordinates: { lat: number; lng: number };
  customer_tambak_pelanggan: string | number;
  customer_data?: any;
  original_interaction_id: number;
  original_interaction_data: CustomerInteraction;
  created_at: string;
  updated_at: string;
  visit_purpose?: string;
  farm_visit_name?: string;
  farm_visit_phone?: string;
  farm_visit_position?: string;
  Progress?: string;
  isFromInteraction?: boolean;
}

/**
 * Maps customer interaction data to a visit data structure for ActiveVisit
 * @param interaction - The customer interaction record
 * @param coordinates - GPS coordinates object (default fallback provided)
 * @param employee_id - Current employee ID (will be filled by the app)
 * @returns Mapped visit data ready for ActiveVisit screen
 */
export const mapInteractionToVisit = (
  interaction: CustomerInteraction,
  coordinates: { lat: number; lng: number } = { lat: 0, lng: 0 },
  employee_id: string = ''
): MappedVisitData => {
  const currentTime = getWIBTimeISO();
  const currentDateTime = new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  // Generate a temporary visit ID
  const tempVisitId = `temp_interaction_${interaction.id}_${Date.now()}`;

  // Create comprehensive visit notes from interaction data
  const visitNotes = [
    `Visit started from Customer Interaction #${interaction.id}`,
    `Original interaction: ${interaction.interaction_type}`,
    interaction.subject && `Subject: ${interaction.subject}`,
    interaction.keterangan && `Details: ${interaction.keterangan}`,
    `Priority: ${interaction.priority}`,
    `Started on: ${currentDateTime}`,
  ].filter(Boolean).join('\n');

  // Determine visit purpose based on interaction type and details
  let visitPurpose = 'Customer visit';
  
  // FIXED: Add null safety check for interaction_type
  const interactionType = interaction.interaction_type || 'Visit';
  
  if (interactionType === 'Visit/Meeting') {
    visitPurpose = interaction.subject || 'Scheduled customer meeting';
  } else {
    // FIXED: Safe toLowerCase() with null check
    const typeLabel = interactionType.toLowerCase ? interactionType.toLowerCase() : 'contact';
    visitPurpose = `Follow-up ${typeLabel} visit`;
  }

  const mappedVisit: MappedVisitData = {
    id: tempVisitId,
    employee_id: employee_id, // Will be filled by the app
    customer_id: interaction.customer_tambak_id || 0,
    customer_name: interaction.customer_name || 'Unknown Customer',
    customer_address: '', // Will be populated from customer lookup
    start_time: currentTime,
    status: 'active' as const,
    notes: visitNotes,
    satisfaction_rating: 0,
    latitude: coordinates.lat,
    longitude: coordinates.lng,
    visit_coordinates: coordinates,
    customer_tambak_pelanggan: interaction.No_pelanggan,
    
    // Store reference to original interaction for later updates
    original_interaction_id: interaction.id,
    original_interaction_data: interaction,
    
    created_at: currentTime,
    updated_at: currentTime,
    visit_purpose: visitPurpose,
    
    // Initialize Farm PIC fields as empty (user can fill them)
    farm_visit_name: '',
    farm_visit_phone: '',
    farm_visit_position: '',
    
    // Initialize Progress as null (user must select)
    Progress: undefined,
    
    // Flag to indicate this visit came from interaction
    isFromInteraction: true
  };

  return mappedVisit;
};

/**
 * Extracts user-friendly field mapping summary for display
 * @param interaction - The customer interaction record
 * @returns Object with pre-filled fields and what needs to be completed
 */
export const getPreFilledFieldsSummary = (interaction: CustomerInteraction) => {
  const preFilledFields = [];
  const needsCompletion = [];

  // Customer Information (always pre-filled)
  preFilledFields.push({
    field: 'Customer Name',
    value: interaction.customer_name,
    source: 'From interaction record'
  });

  preFilledFields.push({
    field: 'Customer ID',
    value: interaction.No_pelanggan,
    source: 'From interaction record'
  });

  // Visit Notes (pre-filled with interaction context)
  preFilledFields.push({
    field: 'Visit Notes (Initial)',
    value: 'Basic visit context from interaction',
    source: 'Auto-generated from interaction details'
  });

  // Visit Purpose (pre-filled based on interaction type)
  preFilledFields.push({
    field: 'Visit Purpose',
    value: interaction.interaction_type === 'Visit/Meeting' 
      ? (interaction.subject || 'Scheduled customer meeting')
      : `Follow-up ${interaction.interaction_type.toLowerCase()} visit`,
    source: 'Based on interaction type and subject'
  });

  // Fields that need completion
  needsCompletion.push({
    field: 'Arrival Selfie',
    required: true,
    description: 'Required photo evidence'
  });

  needsCompletion.push({
    field: 'Visit Notes (Complete)',
    required: true,
    description: 'Add detailed notes about the visit'
  });

  needsCompletion.push({
    field: 'Progress Status',
    required: true,
    description: 'Select current progress status'
  });

  needsCompletion.push({
    field: 'Farm PIC Details',
    required: false,
    description: 'Optional: Name, phone, and position'
  });

  return {
    preFilledFields,
    needsCompletion,
    summary: {
      totalPreFilled: preFilledFields.length,
      totalRequired: needsCompletion.filter(field => field.required).length,
      totalOptional: needsCompletion.filter(field => !field.required).length
    }
  };
};

/**
 * Validates that interaction data is suitable for visit creation
 * @param interaction - The customer interaction record
 * @returns Validation result with success status and any error messages
 */
export const validateInteractionForVisit = (interaction: CustomerInteraction | null) => {
  const errors: string[] = [];

  if (!interaction) {
    errors.push('No interaction data provided');
    return { isValid: false, errors };
  }

  // Required fields validation - Made more flexible to handle real data
  if (!interaction.No_pelanggan || interaction.No_pelanggan.trim() === '') {
    // Still validate but provide more helpful message
    console.warn('⚠️ Customer ID missing but allowing visit creation:', interaction);
  }

  if (!interaction.customer_name || interaction.customer_name.trim() === '') {
    // Try alternative customer name fields
    const hasAlternativeName = interaction.subject && interaction.subject.includes('customer') ||
                              interaction.keterangan && interaction.keterangan.length > 0;
    
    if (!hasAlternativeName) {
      errors.push('Customer information is insufficient for visit creation');
    }
  }

  // PIC validation - Made optional as it's not always required
  if (!interaction.PIC || interaction.PIC.trim() === '') {
    console.warn('⚠️ PIC missing but allowing visit creation:', interaction.No_pelanggan);
  }

  // Business logic validation - Fixed status check
  if (interaction.status === 'completed') {
    errors.push('Cannot start visit for already completed interaction');
  }

  // Visit ID check - Allow if visit_id is 0, null, or undefined
  if (interaction.visit_id && interaction.visit_id > 0) {
    errors.push('Interaction already has an associated visit');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Creates user feedback message about the pre-filled data
 * @param interaction - The customer interaction record
 * @returns User-friendly message explaining what was pre-filled
 */
export const createPreFilledDataMessage = (interaction: CustomerInteraction): string => {
  const summary = getPreFilledFieldsSummary(interaction);

  return [
    `✅ Pre-filled ${summary.summary.totalPreFilled} fields from interaction:`,
    `📝 Customer: ${interaction.customer_name} (${interaction.No_pelanggan})`,
    `🎯 Purpose: ${interaction.interaction_type} follow-up`,
    ``,
    `⚠️ Still required: ${summary.summary.totalRequired} fields`,
    `• Arrival selfie (required)`,
    `• Complete visit notes (required)`,
    `• Progress status selection (required)`,
    ``,
    `📋 Optional: Farm PIC details can be added during the visit`
  ].join('\n');
};